import React, { useReducer } from 'react'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import {
  checkValidTimeSlot,
  getEarliestCollectionTime,
  parseDateString
} from 'src/utils/date'
import modalController from 'src/decorators/ModalController'
import { isEmpty } from 'src/utils/string'
import { isObjectEmpty } from 'src/utils/object'
import useAppSelector from 'src/hooks/useAppSelector'
import {
  fetchTimeSlotsAction,
  resetAppointmentAction
} from 'src/redux/order/action'
import { useRouter } from 'next/router'
import { selectOrder } from 'src/redux/order'

type Action =
  | { type: 'SET_DESKTOP_HEIGHT'; payload: number }
  | { type: 'SET_MOBILE_HEIGHT'; payload: number }
  | { type: 'SET_SHOW_PREFERRED_DATE_MODAL'; payload: boolean }

interface State {
  desktopHeight: number
  mobileHeight: number
  showPreferredDateModal: boolean
}

const OrderingFlowContext = React.createContext({
  storeOrder: {},
  isValidDateTime: null,
  isCountdownTimeStart: false,
  dispatch: null,
  mobileHeight: 0,
  desktopHeight: 0,
  resetAppointmentAction: () => undefined,
  durationInMs: null,
  showPreferredDateModal: false,
  setShowPreferredDateModal: (payload: boolean) => undefined,
  setMobileHeight: (payload: number) => undefined,
  setDesktopHeight: (payload: number) => undefined
})

const initialState: State = {
  desktopHeight: 80,
  mobileHeight: 60,
  showPreferredDateModal: false
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_DESKTOP_HEIGHT':
      return {
        ...state,
        desktopHeight: action.payload
      }
    case 'SET_MOBILE_HEIGHT':
      return {
        ...state,
        mobileHeight: action.payload
      }
    case 'SET_SHOW_PREFERRED_DATE_MODAL':
      return {
        ...state,
        showPreferredDateModal: action.payload
      }
    default:
      throw new Error()
  }
}

export const OrderingFlowContextProvider = React.memo(({ children }) => {
  const [isAppointmentReady, setIsAppointmentReady] =
    React.useState<boolean>(false)
  const [isValidDateTime, setIsValidDateTime] = React.useState<null | boolean>(
    null
  )
  const [isParamsExist, setIsParamsExist] = React.useState<null | boolean>(null)
  const [isCountdownTimeStart, setIsCountdownTimeStart] =
    React.useState<boolean>(false)
  const [earliestCollectionTime, setEarliestCollectionTime] = React.useState<
    string | Date
  >(null)
  const [durationInMs, setDurationInMs] = React.useState<null | number>(null)
  const [storeOrderState, setStoreOrderState] = React.useState(null)
  const { storeOrder } = useAppSelector(selectOrder)
  const { data } = storeOrder
  const dispatchFromRedux = useAppDispatch()
  const [state, dispatch] = useReducer(reducer, initialState)
  const setDesktopHeight = (payload: number) =>
    dispatch({ type: 'SET_DESKTOP_HEIGHT', payload })
  const setMobileHeight = (payload: number) =>
    dispatch({ type: 'SET_MOBILE_HEIGHT', payload })
  const setShowPreferredDateModal = (payload: boolean) =>
    dispatch({ type: 'SET_SHOW_PREFERRED_DATE_MODAL', payload })
  const router = useRouter()

  React.useEffect(() => {
    if (!storeOrderState && !isObjectEmpty(data) && !router.query?.collectionDate) {
      setStoreOrderState(data)
    }
  }, [storeOrderState, data, router.query])

  React.useEffect(() => {
    const {
      address,
      collectionMethod,
      collectionTimeSlot,
      collectionDate: collectionDateSlot
    } = router.query as Record<string, string | undefined>

    if (
      // check this when using next.js built-in localisation, the pathname might be changed.
      (router.pathname === '/[locale]' || router.pathname === '/[locale]/menus/[slug]') &&
      !isObjectEmpty(storeOrderState) && !router.query?.collectionDate
    ) {
      (async () => {
        const {
          isPickup,
          storeAddress,
          defaultStoreId,
          deliveryAddress,
          collectionDate,
          collectionTime
        } = storeOrderState
        const isAppointmentReadyTemp =
          (isPickup && !!storeAddress && !defaultStoreId) ||
          (!isPickup && !!deliveryAddress)
        if (
          typeof window !== 'undefined' &&
          !window.localStorage.getItem('lastTimeTouchOrder') &&
          isAppointmentReadyTemp
        ) {
          window.localStorage.setItem('lastTimeTouchOrder', new Date().toString())
        }
        setIsAppointmentReady(isAppointmentReadyTemp)
        const timeSlots = await dispatchFromRedux(
          fetchTimeSlotsAction({
            date: parseDateString(collectionDate)
          })
        )
        if (timeSlots.meta.requestStatus === 'fulfilled' && timeSlots.payload.payload.timeIntervals) {
          const newDate = new Date(collectionDate)
          const { isValidTimeSlot } = checkValidTimeSlot({
            date: newDate,
            currentTimeSlot: collectionTime,
            availableTimeSlots: timeSlots.payload.payload.timeIntervals
          })
          // store validateTime in state
          setIsValidDateTime(isValidTimeSlot)
          if (isValidTimeSlot) {
            setEarliestCollectionTime(
              getEarliestCollectionTime({
                collectionTime,
                availableTimeSlots: timeSlots.payload.payload.timeIntervals
              })
            )
          }
        }
        setIsParamsExist(
          !isEmpty(address) &&
            !isEmpty(collectionDateSlot) &&
            !isEmpty(collectionMethod) &&
            !isEmpty(collectionTimeSlot)
        )
      })()
    }
  }, [router.pathname, storeOrderState, dispatchFromRedux, router.query])

  React.useEffect(() => {
    if ((router.pathname === '/[locale]/menus/[slug]' || router.pathname === '/[locale]') && isAppointmentReady) {
      // check this when using next.js built-in localisation, the pathname might be changed.
      const lastTimeTouchOrder = new Date(window.localStorage.getItem('lastTimeTouchOrder'))
      const TWO_HOURS_IN_MS = 7.2e6
      if (
        (isParamsExist === false && isValidDateTime === false) ||
        (isParamsExist === false &&
          lastTimeTouchOrder.getTime() + TWO_HOURS_IN_MS <= new Date().getTime())
      ) {
        modalController.showModal('ReminderOrderModal')
      }
      if (earliestCollectionTime !== null) {
        const getTimeFromEarliestCollectionTime = new Date(
          earliestCollectionTime
        )
        const currentTime = new Date()
        // countdown timer will appear 5 minutes before time slot closing to expired.
        if (
          currentTime.getTime() >=
          getTimeFromEarliestCollectionTime.getTime() - 300000
        ) {
          setDesktopHeight(130)
          setMobileHeight(135)
          setIsCountdownTimeStart(true)
          setDurationInMs(
            getTimeFromEarliestCollectionTime.getTime() - currentTime.getTime()
          )
        }
      }
    }
  }, [
    isAppointmentReady,
    isValidDateTime,
    isParamsExist,
    earliestCollectionTime,
    router.pathname
  ])

  const orderingFlowContextProvider = React.useMemo(
    () => ({
      storeOrder,
      isValidDateTime,
      isCountdownTimeStart,
      dispatch,
      mobileHeight: state.mobileHeight,
      desktopHeight: state.desktopHeight,
      resetAppointmentAction: () => dispatchFromRedux(resetAppointmentAction()),
      durationInMs,
      showPreferredDateModal: state.showPreferredDateModal,
      setDesktopHeight,
      setMobileHeight,
      setShowPreferredDateModal
    }),
    [
      storeOrder,
      isValidDateTime,
      isCountdownTimeStart,
      dispatch,
      durationInMs,
      dispatchFromRedux,
      state
    ]
  )

  return (
    <OrderingFlowContext.Provider value={orderingFlowContextProvider}>
      {children}
    </OrderingFlowContext.Provider>
  )
})

export default OrderingFlowContext
